import { FormattedMessage } from 'react-intl';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import PermissionButton from 'components/buttons/PermissionButton';
import { SavingsOutlined as SavingsOutlinedIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import {
    useLazyGetAllEmployeesListWithFiltersQuery,
    useLazyGetEmployeesListWithFiltersQuery,
} from 'store/api/employees';
import { useStartPrepaymentExportMutation } from 'store/api/exports';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import {
    useLazyGetLinkedInvoicesForPrepaymentsQuery,
    useLazyGetPrepaymentsCountQuery,
    useLazyGetPrepaymentsWithFiltersQuery,
} from 'store/api/prepayments';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features';
import { InvoiceStatusType } from 'types/invoices';
import { PrepaymentListItem } from 'types/prepayments';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAsyncExport } from 'utils/useAsyncExport';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import {
    PrepaymentAmountCell,
    PrepaymentCaseCell,
    PrepaymentLinkedInvoices,
    PrepaymentNumberClientPayer,
    PrepaymentRetainerBalanceCell,
} from './PrepaymentTableCells';

const USED_CLASSIFIERS = [
    'ACCOUNTING_REVENUE_TYPE',
    'INVOICE_STATUS_TYPE',
    'INVOICE_TYPE',
    'CURRENCY',
    'COUNTRY',
    'BUSINESS_AREA_TYPE',
];

const quickFilterStatusTypes = [
    InvoiceStatusType.DRAFT,
    InvoiceStatusType.REVIEWED,
    InvoiceStatusType.APPROVED,
    InvoiceStatusType.FINALIZED,
    InvoiceStatusType.SENT,
    InvoiceStatusType.PAID,
];

const READ_COMMENTS_PERMISSION = ['READ.INVOICES.COMMENTS'];

const getPrepaymentHref = (prepayment: PrepaymentListItem) => `/invoice/${prepayment.id}`;

const PrepaymentsTable = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });
    const [fetchData, { data, isFetching }] = useLazyGetPrepaymentsWithFiltersQuery();
    const hasPermissionToExportInvoices = useHasPermissions(['EXPORT.INVOICES']);
    const canReadComments = useHasPermissions(READ_COMMENTS_PERMISSION);
    const isExcludeFromQlikSenseEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_EXCLUDE_FROM_QLIK_SENSE);

    const { isBusy: isExportBusy, triggerManualDownload } = useAsyncExport(useStartPrepaymentExportMutation, {
        entityType: 'PREPAYMENT',
        entityName: t('prepayments.export.entity-name'),
        fileName: EXPORT_CONFIG.PREPAYMENT.fileName,
    });

    const invoiceStatusTypeOptions = useMemo(
        () =>
            classifiers
                ? getFilterOptionsFromClassifier(classifiers?.['INVOICE_STATUS_TYPE']).map((opt) => ({
                      ...opt,
                      argument: opt.value,
                  }))
                : [],
        [classifiers]
    );

    const invoiceStatusTypeQuickFilterOptions = useMemo(
        () => (classifiers ? mapToFilterOptions(quickFilterStatusTypes, classifiers?.['INVOICE_STATUS_TYPE']) : []),
        [classifiers]
    );

    const accountingRevenueTypeOptions = useMemo(
        () => (classifiers ? getFilterOptionsFromClassifier(classifiers?.['ACCOUNTING_REVENUE_TYPE']) : []),
        [classifiers]
    );

    const columns = useMemo<DataTableColumnDef<PrepaymentListItem>[]>(
        () => [
            {
                header: t('invoices.invoice-no-client-payer'),
                id: 'invoiceNumber',
                enableGrouping: false,
                accessorKey: 'invoiceNumber',
                meta: {
                    widthWeight: 1.22,
                    minPx: 122,
                },
                cell: ({ row }) => (
                    <PrepaymentNumberClientPayer prepayment={row.original} classifiers={classifiers ?? {}} />
                ),
            },
            {
                header: t('matters.name'),
                id: 'matters',
                enableSorting: false,
                meta: {
                    widthWeight: 1.19,
                    minPx: 147,
                },
                cell: ({ row }) => <PrepaymentCaseCell prepayment={row.original} />,
            },
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                meta: {
                    widthWeight: 1,
                    minPx: 123,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                meta: {
                    widthWeight: 1,
                    minPx: 123,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('prepayments.vat-total'),
                id: 'vatTotal',
                accessorKey: 'vatTotal',
                meta: {
                    widthWeight: 1.1,
                    minPx: 135,
                    sortAccessor: 'totals.baseCurrency.invoice.vat',
                },
                cell: ({ row }) => (
                    <PrepaymentAmountCell
                        amount={row.original.vatTotal}
                        currencyTypeCode={row.original.paymentCurrencyTypeCode}
                        amountBaseCurrency={row.original.vatTotalBaseCurrency}
                        baseCurrencyTypeCode={row.original.baseCurrencyTypeCode}
                    />
                ),
            },
            {
                header: t('prepayments.gross-total'),
                id: 'grossTotal',
                accessorKey: 'grossTotal',
                meta: {
                    widthWeight: 1.36,
                    minPx: 167,
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithVat',
                },
                cell: ({ row }) => (
                    <PrepaymentAmountCell
                        amount={row.original.grossTotal}
                        currencyTypeCode={row.original.paymentCurrencyTypeCode}
                        amountBaseCurrency={row.original.grossTotalBaseCurrency}
                        baseCurrencyTypeCode={row.original.baseCurrencyTypeCode}
                    />
                ),
            },
            {
                header: t('prepayments.paid-on'),
                id: 'paidOnDate',
                accessorKey: 'paidOnDate',
                meta: {
                    widthWeight: 1,
                    minPx: 123,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value ? <DateDefaultNowrap dateValue={value} /> : <p className="text-body1">-</p>;
                },
            },
            {
                header: t('prepayments.linked-invoices'),
                id: 'linkedInvoices',
                enableSorting: false,
                meta: {
                    widthWeight: 2.29,
                    // Widest floor of the table: the cell stacks nowrap "<invoice no.> — <amount>"
                    // lines, so anything narrower clips instead of wrapping.
                    minPx: 281,
                },
                cell: ({ row }) => (
                    <PrepaymentLinkedInvoices
                        linkedInvoices={row.original.linkedInvoices}
                        currencyTypeCode={row.original.paymentCurrencyTypeCode}
                        classifiers={classifiers ?? {}}
                    />
                ),
            },
            {
                header: t('prepayments.used-amount'),
                id: 'usedAmount',
                accessorKey: 'usedAmount',
                meta: {
                    widthWeight: 1.02,
                    minPx: 126,
                    sortAccessor: 'sortParamsJson.prepaymentUsedAmountBaseCurrency',
                },
                cell: ({ row }) => (
                    <PrepaymentAmountCell
                        amount={row.original.usedAmount}
                        currencyTypeCode={row.original.paymentCurrencyTypeCode}
                        amountBaseCurrency={row.original.usedAmountBaseCurrency}
                        baseCurrencyTypeCode={row.original.baseCurrencyTypeCode}
                    />
                ),
            },
            {
                header: t('prepayments.remaining-amount'),
                id: 'remainingAmount',
                accessorKey: 'remainingAmount',
                meta: {
                    widthWeight: 1.36,
                    minPx: 167,
                    sortAccessor: 'sortParamsJson.prepaymentRemainingAmountBaseCurrency',
                },
                cell: ({ row }) => {
                    const isNegative = Number(row.original.remainingAmount) < 0;
                    return (
                        <PrepaymentAmountCell
                            amount={row.original.remainingAmount}
                            currencyTypeCode={row.original.paymentCurrencyTypeCode}
                            amountBaseCurrency={row.original.remainingAmountBaseCurrency}
                            baseCurrencyTypeCode={row.original.baseCurrencyTypeCode}
                            className={isNegative ? 'text-error' : undefined}
                        />
                    );
                },
            },
            {
                header: () => (
                    <div className="flex items-center gap-1">
                        {t('prepayments.retainer-balance')}
                        <InfoTooltip text="prepayments.retainer-balance-tooltip" nested />
                    </div>
                ),
                id: 'retainerBalance',
                accessorKey: 'retainerBalance',
                meta: {
                    widthWeight: 1.35,
                    minPx: 166,
                    sortAccessor: 'sortParamsJson.retainerBalanceBaseCurrency',
                },
                cell: ({ row }) => <PrepaymentRetainerBalanceCell prepayment={row.original} />,
            },
        ],
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'invoiceStatusTypeCode',
                    chipLabel: 'filters.status',
                    filters: invoiceStatusTypeQuickFilterOptions,
                    multiple: true,
                },
            ],
            filters: [
                // -------- INVOICE --------
                {
                    type: 'dynamicmultiselect',
                    label: t('invoices.filter.invoiced-office'),
                    selector: 'invoicedByOffice.id',
                    group: 'prepayments.filter.invoice-group',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'daterange',
                    selector: 'invoiceDate',
                    group: 'prepayments.filter.invoice-group',
                    label: t('invoices.filter.invoice-date-from'),
                    labelTo: t('invoices.filter.invoice-date-until'),
                    shouldDisplayQuickPick: true,
                },
                {
                    type: 'daterange',
                    selector: 'createdDate',
                    group: 'prepayments.filter.invoice-group',
                    label: t('invoices.filter.invoice-created-from'),
                    labelTo: t('invoices.filter.invoice-created-until'),
                    shouldDisplayQuickPick: true,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('invoices.filter.created-by'),
                    selector: 'createdByEmployee.id',
                    group: 'prepayments.filter.invoice-group',
                    paginated: {
                        // Every employee, not only the active ones: a leaver's invoices stay findable.
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'checkboxgroup',
                    selector: 'createdByEmployee.id',
                    group: 'prepayments.filter.invoice-group',
                    comperator: '=isnull=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.created-by-system-tooltip',
                            label: t('invoices.filter.created-by-system'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'daterange',
                    selector: 'paidOnDate',
                    group: 'prepayments.filter.invoice-group',
                    label: t('invoices.filter.paid-date-from'),
                    labelTo: t('invoices.filter.paid-date-until'),
                    shouldDisplayQuickPick: true,
                },
                {
                    type: 'daterange',
                    selector: 'lastPaymentDate',
                    group: 'prepayments.filter.invoice-group',
                    label: t('invoices.filter.last-payment-from'),
                    labelTo: t('invoices.filter.last-payment-until'),
                    shouldDisplayQuickPick: true,
                },
                {
                    type: 'interval',
                    label: t('invoices.filter.total-amount'),
                    selector: 'totalAmountInPaymentCurrency',
                    group: 'prepayments.filter.invoice-group',
                    minLabel: t('invoices.filter.total-amount-lowest'),
                    maxLabel: t('invoices.filter.total-amount-highest'),
                },
                {
                    type: 'interval',
                    label: t('prepayments.filter.gross-total'),
                    selector: 'totalAmountInPaymentCurrency',
                    group: 'prepayments.filter.invoice-group',
                    minLabel: t('prepayments.filter.gross-total-from'),
                    maxLabel: t('prepayments.filter.gross-total-to'),
                },
                {
                    type: 'interval',
                    label: t('prepayments.remaining-amount'),
                    selector: 'sortParamsJson',
                    comperator: '=remainingamountrange=',
                    group: 'prepayments.filter.invoice-group',
                    minLabel: t('prepayments.filter.remaining-amount-from'),
                    maxLabel: t('prepayments.filter.remaining-amount-to'),
                },
                {
                    type: 'interval',
                    label: t('prepayments.retainer-balance'),
                    selector: 'sortParamsJson',
                    comperator: '=retainerbalancerange=',
                    group: 'prepayments.filter.invoice-group',
                    minLabel: t('prepayments.filter.retainer-balance-from'),
                    maxLabel: t('prepayments.filter.retainer-balance-to'),
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.invoice-currency'),
                    selector: 'invoiceCurrencyTypeCode',
                    group: 'prepayments.filter.invoice-group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.payment-currency'),
                    selector: 'paymentCurrencyTypeCode',
                    group: 'prepayments.filter.invoice-group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.invoice-status'),
                    selector: 'invoiceStatusTypeCode',
                    group: 'prepayments.filter.invoice-group',
                    options: invoiceStatusTypeOptions,
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.accounting-revenue-type'),
                    selector: 'accountingRule.accountingRevenueTypeCode',
                    group: 'prepayments.filter.invoice-group',
                    options: accountingRevenueTypeOptions,
                    getShouldHide: () => accountingRevenueTypeOptions.length <= 1,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('prepayments.filter.linked-invoices'),
                    selector: 'id',
                    group: 'prepayments.filter.invoice-group',
                    comperator: '=haslinkedinvoice=',
                    paginated: {
                        fetch: useLazyGetLinkedInvoicesForPrepaymentsQuery,
                        searchField: 'invoiceNumber',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'invoiceNumber',
                    },
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    group: 'prepayments.filter.invoice-group',
                    comperator: '=isnotlinked=',
                    filters: [
                        {
                            tooltip: 'prepayments.filter.not-linked-tooltip',
                            label: t('prepayments.filter.not-linked'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    label: t('invoices.filter.comments'),
                    selector: 'id',
                    group: 'prepayments.filter.invoice-group',
                    comperator: '=hascomments=',
                    multiple: false,
                    filters: [
                        {
                            tooltip: 'invoices.filter.comments-all-tooltip',
                            label: t('invoices.filter.comments-all'),
                            argument: 'all',
                        },
                        {
                            tooltip: 'invoices.filter.comments-open-tooltip',
                            label: t('invoices.filter.comments-open'),
                            argument: 'open',
                        },
                    ],
                    getShouldHide: () => !canReadComments,
                },
                // -------- CASE --------
                {
                    type: 'checkboxgroup',
                    selector: 'invoiceMatters.matter',
                    group: 'prepayments.filter.case-group',
                    comperator: '=ismyinvoicematter=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.my-cases-tooltip',
                            label: t('invoices.filter.my-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'invoiceMatters.matter',
                    group: 'prepayments.filter.case-group',
                    comperator: '=ismyteaminvoicematter=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.my-team-cases-tooltip',
                            label: t('invoices.filter.my-team-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    id: 'matterIdInFilter',
                    type: 'dynamicmultiselect',
                    group: 'prepayments.filter.case-group',
                    label: t('invoices.filter.case-name-number'),
                    selector: 'invoiceMatters.matter.id',
                    canExclude: true,
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                    },
                },
                {
                    type: 'text',
                    label: t('invoices.filter.matter-reference'),
                    selector: 'invoiceMatters.matter.matterClientReference',
                    group: 'prepayments.filter.case-group',
                },
                // -------- CLIENT --------
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    group: 'invoices.filter.client-group',
                    label: t('invoices.filter.client-name'),
                    selector: 'invoiceMatters.matter.clientParty.id',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'invoices.filter.client-group',
                    label: t('invoices.filter.relationship-partner'),
                    selector: 'invoiceMatters.matter.clientParty.partners.employee.id',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'invoices.filter.client-group',
                    label: t('invoices.filter.relationship-manager'),
                    selector: 'invoiceMatters.matter.clientParty.managers.employee.id',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.country'),
                    selector: 'invoiceMatters.matter.clientParty.addresses.countryCode',
                    group: 'invoices.filter.client-group',
                    canExclude: true,
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('invoices.filter.client-group-label'),
                    selector: 'invoiceMatters.matter.clientParty.partyGroupParties.partyGroup.id',
                    group: 'invoices.filter.client-group',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetMinGroupsListWithFiltersQuery,
                        searchField: 'groupName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'groupName',
                    },
                },
                // -------- PAYER --------
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    group: 'invoices.filter.payer-group',
                    label: t('invoices.filter.payer-name'),
                    selector: 'clientParty.id',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    group: 'invoices.filter.payer-group',
                    label: t('invoices.filter.payer-business-areas'),
                    selector: 'clientParty.businessAreas.businessAreaCode',
                    canExclude: true,
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('invoices.filter.payer-group-label'),
                    selector: 'clientParty.partyGroupParties.partyGroup.id',
                    group: 'invoices.filter.payer-group',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetMinGroupsListWithFiltersQuery,
                        searchField: 'groupName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'groupName',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('invoices.filter.country'),
                    selector: 'clientParty',
                    comperator: '=billingaddresscountry=',
                    group: 'invoices.filter.payer-group',
                    canExclude: true,
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                },
                {
                    type: 'dropdown',
                    label: t('invoices.filter.deadline-day'),
                    selector: 'clientParty.invoicingDeadlineDay',
                    group: 'invoices.filter.payer-group',
                    comperator: '=invoicingdeadlineday=',
                    options: [
                        { label: 'Date missed', value: -1 },
                        { label: 'Today', value: new Date().getDate() },
                        ...Array.from({ length: 31 }, (_, i) => ({
                            label: (i + 1).toString(),
                            value: i + 1,
                        })),
                    ],
                },
                // -------- OTHER --------
                {
                    type: 'checkboxgroup',
                    selector: 'invoiceMatters.matter.supervisedByEmployee.id',
                    group: 'prepayments.filter.other-group',
                    comperator: '=isinactivesupervisingemployee=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.inactive-supervising-partner-tooltip',
                            label: t('invoices.filter.inactive-supervising-partner'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'invoiceMatters.matter.responsibleEmployee.id',
                    group: 'prepayments.filter.other-group',
                    comperator: '=isinactiveresponsiblelawyer=',
                    filters: [
                        {
                            tooltip: 'invoices.filter.inactive-responsible-lawyer-tooltip',
                            label: t('invoices.filter.inactive-responsible-lawyer'),
                            argument: 'true',
                        },
                    ],
                },
                ...(isExcludeFromQlikSenseEnabled
                    ? [
                          {
                              type: 'checkboxgroup' as const,
                              selector: 'excludeFromReporting',
                              group: 'prepayments.filter.other-group' as const,
                              comperator: '=isnottrue=',
                              filters: [
                                  {
                                      tooltip: 'invoices.filter.exclude-from-report-tooltip',
                                      label: t('invoices.filter.exclude-from-report'),
                                      argument: 'true',
                                  },
                              ],
                          },
                      ]
                    : []),
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    group: 'prepayments.filter.other-group',
                    comperator: '=includeOldInvoices=',
                    filters: [
                        {
                            tooltip: 'prepayments.filter.include-old-prepayments-tooltip',
                            label: t('prepayments.filter.include-old-prepayments'),
                            argument: 'true',
                        },
                    ],
                },
            ],
            searchBarSelectors: [
                { selector: 'invoiceNumber' },
                { selector: 'clientParty.name' },
                { selector: 'invoiceMatters.matter.clientParty.name' },
            ],
        }),
        [
            classifiers,
            invoiceStatusTypeOptions,
            invoiceStatusTypeQuickFilterOptions,
            accountingRevenueTypeOptions,
            isExcludeFromQlikSenseEnabled,
            canReadComments,
        ]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<PrepaymentListItem>
            getRowHref={getPrepaymentHref}
            columnConfigId="prepayments"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="prepayments.prepayments"
            singularTextId="prepayments.prepayment"
            noEntriesId="prepayments.no-prepayments-found"
            noEntriesBodyId="prepayments.empty.body"
            noEntriesIcon={SavingsOutlinedIcon}
            noEntriesAction={
                <PermissionButton
                    permissions={[
                        'CREATE.INVOICES.TYPE_PREPAYMENT_WITH_MATTER_ACCESS',
                        'CREATE.INVOICES.MATTER_ACCESS',
                    ]}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/billable-clients')}
                >
                    <FormattedMessage id="billable-clients.go-to-billable-clients" />
                </PermissionButton>
            }
            fetchData={fetchData}
            fetchCount={useLazyGetPrepaymentsCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            exportHookResults={
                hasPermissionToExportInvoices
                    ? [{ labelKey: 'export', isBusy: isExportBusy, triggerManualDownload }]
                    : undefined
            }
        />
    );
};

export default PrepaymentsTable;
